/**
 * Ridgewill Family Groups, as the portal shows them (RidgeWill/account#117,
 * #118, over RidgeWill/auth#614, #615 and #616).
 *
 * A module rather than JSX for the reason `lib/connections.ts` gives: every
 * sentence here is a claim about who can see or do what for whom, and a pure
 * function can be tested against every shape the service sends.
 *
 * ── THE RULES ARE AUTH'S ─────────────────────────────────────────────
 *
 * Who may start a group (21 or older), who may join as a dependent (under 18),
 * the last-guardian rule, the acknowledgment at 18: auth decides all of them
 * and refuses with a code and a sentence. The portal only decides what to
 * OFFER, from what auth reports (`mayStart`, `acknowledgmentDue`). It never
 * works out an age itself.
 *
 * ── ANYONE CAN JOIN; GUARDIANS SIGN ONLY FOR UNDER 18 ────────────────
 *
 * Jon, 2026-10-13 (auth#625): a family group brings together the people in a
 * family, whatever their age. Guardians see the same things for every member,
 * and the invitation says so before anyone joins. Guardians accept agreements
 * only for members under 18; from 18 a member accepts their own.
 * `guardianAccepts` is auth's answer to which of those applies; the portal
 * never works out an age.
 *
 * Internally a member is still a `dependent` (auth's role name). The screens
 * say "family member".
 */
import { formatDay } from './subscriptions.js';

export type FamilyRole = 'guardian' | 'dependent';

export interface FamilyPerson {
  userId: string;
  name: string;
  image: string | null;
}

export interface FamilyMember extends FamilyPerson {
  role: FamilyRole;
  joinedAt: string;
  /** A dependent's only: told the acknowledgment at 18 is due, not yet given. */
  acknowledgmentDue: boolean;
  /** A dependent's only: when they took over their own agreements. */
  adultAcknowledgedAt: string | null;
  /** A dependent's only, for their guardians: agreements waiting for a guardian. */
  waitingForGuardian?: number;
  /**
   * A dependent's only: true while guardians accept agreements for them (under
   * 18). False from 18. Absent from an auth before auth#625, which reads as true
   * for a member, the old rule.
   */
  guardianAccepts?: boolean;
}

export interface FamilyInvitation {
  id: string;
  kind: FamilyRole;
  email: string | null;
  expiresAt: string;
}

export interface FamilyGroup {
  id: string;
  myRole: FamilyRole;
  joinedAt: string;
  guardians: FamilyMember[];
  dependents: FamilyMember[];
  /** Open invitations. Sent to guardians only. */
  invitations: FamilyInvitation[];
}

export type NoticeKind =
  | 'member-left'
  | 'dependent-removed'
  | 'removed'
  | 'invitation-declined'
  | 'invitation-refused-adult'
  | 'invitation-refused-minor'
  | 'dependent-adult'
  | 'adult-acknowledgment-due'
  | 'dependent-acknowledged';

export interface FamilyNotice {
  id: string;
  groupId: string;
  kind: NoticeKind | string;
  person: FamilyPerson | null;
  createdAt: string;
  read: boolean;
}

/** `GET /account/family`. */
export interface MyFamily {
  /** May this person start a group: 21 or older, and not a dependent. */
  mayStart: boolean;
  groups: FamilyGroup[];
  notices: FamilyNotice[];
}

/** `POST /account/family/invitation`: what the invitation page shows. */
export interface InvitationPreview {
  kind: FamilyRole;
  inviter: { name: string; image: string | null };
  guardians: { name: string; image: string | null }[];
  expiresAt: string;
}

/** `GET /account/family/dependents/:userId` (auth#616). */
export interface DependentView {
  person: FamilyPerson & { username: string | null };
  joinedAt: string;
  /** True while guardians accept agreements for them (under 18). Absent before auth#625. */
  guardianAccepts?: boolean;
  connections: (FamilyPerson & { username: string | null; connectedSince: string | null; family: boolean })[];
  blocks: (FamilyPerson & { username: string | null })[];
  apps: {
    slug: string;
    name: string;
    settings: { key: string; label: string; type: string; value: unknown; options?: string[] | null }[];
    perConnection: { userId: string; key: string; value: unknown }[];
  }[];
}

/** One agreement on a dependent's page (auth#615). */
export interface DependentAgreement {
  agreementSlug: string;
  name: string;
  appSlug: string | null;
  version: string;
  agreementVersionId: string;
  content: string;
  contentFormat: string;
  summary: string;
  self: { accepted: boolean; acceptedAt: string | null };
  guardian: { accepted: boolean; acceptedAt: string | null; byMe: boolean };
  waitingForGuardian: boolean;
}

// ── Where things live ────────────────────────────────────────────────

export const FAMILY_PATH = '/family';
export const JOIN_PATH = '/family/join';
const DEPENDENT_PREFIX = '/family/dependents/';

export function dependentPath(userId: string): string {
  return `${DEPENDENT_PREFIX}${encodeURIComponent(userId)}`;
}

/** The dependent a path names, or null. */
export function dependentIdOf(path: string): string | null {
  if (!path.startsWith(DEPENDENT_PREFIX)) return null;
  const rest = path.slice(DEPENDENT_PREFIX.length).replace(/\/+$/, '');
  if (!rest || rest.includes('/')) return null;
  try {
    return decodeURIComponent(rest);
  } catch {
    return null;
  }
}

/** The invitation token on the join page's address (`?invitation=`), or null. */
export function invitationTokenOf(search: string): string | null {
  const token = new URLSearchParams(search).get('invitation')?.trim();
  return token ? token : null;
}

// ── Words ────────────────────────────────────────────────────────────

/** A name to show, never empty. A closed account can come back blank. */
export function nameOf(p: { name?: string | null } | null | undefined): string {
  return p?.name?.trim() || 'A Ridgewill member';
}

export function initialOfName(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?';
}

/**
 * What a guardian can see and do, in the same words as the Ridgewill Privacy
 * Policy, section 11 (platform#47). Shown before anyone joins, and on the
 * dependent's own Family Group page, so nobody has to go looking for it.
 *
 * Keyodo's line names the app, which `Connections.test.tsx` forbids for that
 * screen. It is allowed here because it is the privacy policy's own list, and
 * the guardian's Keyodo view is Keyodo's (keyodo#694), described not built here.
 */
export const GUARDIAN_SEES: readonly string[] = [
  'Your connections and the people you block',
  'Which Ridgewill apps you use, and your settings in them',
  'The agreements you have accepted',
  'In Keyodo: the groups you borrow in, who owns each, and your completed trips. Not your photos, notes or requests.',
];

export const GUARDIAN_DOES: readonly string[] = [
  "Accept agreements for you while you're under 18. You accept them too, in your own name. From 18 you accept your own.",
  'Remove you from the Family Group.',
];

export const GUARDIAN_NEVER =
  'A guardian never sees your messages, anything you write in an app, your sign-in activity or your support requests.';

/**
 * Agreements, said on the invitation page and not only in the Terms. The page
 * does not know the invitee's age, so it states both cases.
 */
export function agreementsLine(guardianName: string): string {
  return (
    `If you're under 18, ${guardianName} accepts Ridgewill's and apps' agreements for you, and you accept them ` +
    "too. If you're 18 or older, you accept your own and no one can accept them for you."
  );
}

/** The sentence for a notice, or null for a kind this build does not know. */
export function noticeText(n: FamilyNotice): string | null {
  const who = nameOf(n.person);
  switch (n.kind) {
    case 'member-left':
      return `${who} left your Family Group.`;
    case 'dependent-removed':
      return `${who} was removed from your Family Group.`;
    case 'removed':
      return "You were removed from your Family Group. If you're under 18, anything in a Ridgewill app that needs a parent or guardian is locked until another Family Group adds you.";
    case 'invitation-declined':
      return 'Someone you invited declined the invitation.';
    case 'invitation-refused-adult':
      return 'An invitation could not go through: the account is 18 or older. Family Groups are for people under 18.';
    case 'invitation-refused-minor':
      return "An invitation could not go through: you sent it for someone 18 or older, but they're under 18. If you're their parent or legal guardian, send a new one and say they're under 18.";
    case 'dependent-adult':
      return `${who} is now 18 and accepts their own agreements.`;
    case 'adult-acknowledgment-due':
      return 'You are now 18 and accept your own agreements. Confirm that you take over the ones your guardian accepted for you. You can stay in your Family Group.';
    case 'dependent-acknowledged':
      return `${who} now handles their own agreements.`;
    default:
      return null;
  }
}

/** The line under a member's name. */
export function memberDetail(m: FamilyMember, myRole: FamilyRole, format: (iso: string) => string = formatDay): string {
  if (m.role === 'dependent') {
    if (m.adultAcknowledgedAt) return `Handles their own agreements since ${format(m.adultAcknowledgedAt)}.`;
    if (m.acknowledgmentDue) return 'Now 18 and accepts their own agreements.';
    const waiting = myRole === 'guardian' ? (m.waitingForGuardian ?? 0) : 0;
    if (waiting > 0) return waiting === 1 ? '1 agreement waiting for you.' : `${waiting} agreements waiting for you.`;
  }
  return `In the Family Group since ${format(m.joinedAt)}.`;
}

/**
 * What the leave dialog says, by age. Both inputs are auth's answers: `adult`
 * is `!guardianAccepts`, and `acknowledged` is true unless the hand-over
 * acknowledgment is still owed (only by someone who turned 18 in the group).
 * Nothing is typed.
 */
export function leaveCopy(adult: boolean, acknowledged: boolean): { lead: string; check: string | null } {
  if (adult) {
    return {
      lead: 'You can leave your Family Group. Your guardians will be told.',
      check: acknowledged
        ? null
        : "From now on I'm responsible for the Ridgewill Terms, Privacy Policy and app agreements accepted for me, as if I had accepted them myself.",
    };
  }
  return {
    lead: "You're under 18. You can leave, but anything that needs a guardian, like borrowing in Keyodo, stays locked until another Family Group adds you. Your guardians will be told.",
    check: 'I understand I lose access to anything that needs a guardian until another Family Group adds me.',
  };
}

/** The acknowledgment at 18, staying in the group. */
export const ACKNOWLEDGMENT =
  "From now on I'm responsible for the Ridgewill Terms, Privacy Policy and app agreements accepted for me, as if I had accepted them myself.";

/**
 * The checkbox for accepting one document for a dependent (account#118): an
 * agreement is agreed to "for Sam and for myself"; a privacy policy is read.
 */
export function acceptForLabel(a: { agreementSlug: string; name: string }, dependentName: string): string {
  return /privacy/i.test(a.agreementSlug)
    ? `I have read the ${a.name} for ${dependentName}.`
    : `I agree to the ${a.name} for ${dependentName} and for myself.`;
}

/** A refusal code from auth that the portal says in its own words. */
export function refusalText(code: string | undefined, fallback: string): string {
  switch (code) {
    case 'MEMBER_ACCEPTS_OWN':
      return 'This member is 18 or older and accepts their own agreements.';
    case 'TOO_YOUNG_TO_GUARD':
      return 'Family Groups are for parents and guardians 21 or older.';
    case 'ALREADY_A_DEPENDENT':
      return 'You are already in a Family Group. Leave it before joining another.';
    case 'INVITATION_EXPIRED':
      return 'This invitation has expired. Ask for a new one.';
    case 'INVITATION_NOT_FOUND':
      return 'This invitation is not open any more. It may have been used, withdrawn or declined.';
    case 'BIRTH_DATE_REQUIRED':
      return 'Add your date of birth to your account first.';
    default:
      return fallback;
  }
}
